import { useState, useEffect } from 'react';

/**
 * Budget type options
 */
export type BudgetType = 'none' | 'simple' | 'monthly';

/**
 * Individual budget category permissions
 */
export interface BudgetPermissions {
  costBudgetEnabled: boolean;
  salesBudgetEnabled: boolean;
  resourceBudgetEnabled: boolean;
}

/**
 * Complete budget settings including type and permissions
 */
export interface BudgetSettings {
  budgetType: BudgetType;
  permissions: BudgetPermissions;
}

const STORAGE_KEY = 'budget_settings';

const DEFAULT_SETTINGS: BudgetSettings = {
  budgetType: 'simple',
  permissions: {
    costBudgetEnabled: true,
    salesBudgetEnabled: true,
    resourceBudgetEnabled: true,
  },
};

// Always enabled settings - overrides localStorage
const ALWAYS_ENABLED_SETTINGS: BudgetSettings = {
  budgetType: 'simple',
  permissions: {
    costBudgetEnabled: true,
    salesBudgetEnabled: true,
    resourceBudgetEnabled: true,
  },
};

/**
 * Hook to manage budget settings including budget type and granular permissions
 * 
 * @returns {Object} Budget settings and update functions
 * 
 * @example
 * ```tsx
 * const { settings, updateBudgetType, updatePermissions, isBudgetingEnabled } = useBudgetPermissions();
 * 
 * // Check if budgeting is enabled at all
 * if (!isBudgetingEnabled) {
 *   return null; // Hide budget tab
 * }
 * 
 * // Check if specific budget type is enabled
 * if (settings.permissions.costBudgetEnabled) {
 *   return <CostBudgetView />;
 * }
 * ```
 */
export function useBudgetPermissions() {
  // Always return enabled settings, ignoring localStorage
  const [settings, setSettings] = useState<BudgetSettings>(ALWAYS_ENABLED_SETTINGS);

  // Disabled: No longer saving to localStorage since settings are always enabled
  // useEffect(() => {
  //   try {
  //     localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  //   } catch (error) {
  //     console.error('Failed to save budget settings:', error);
  //   }
  // }, [settings]);

  /**
   * Update the budget type
   */
  const updateBudgetType = (budgetType: BudgetType) => {
    setSettings((prev) => ({
      ...prev,
      budgetType,
    }));
  };

  /**
   * Update budget permissions
   */
  const updatePermissions = (permissions: Partial<BudgetPermissions>) => {
    setSettings((prev) => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        ...permissions,
      },
    }));
  };

  /**
   * Update entire settings object
   */
  const updateSettings = (newSettings: Partial<BudgetSettings>) => {
    setSettings((prev) => ({
      ...prev,
      ...newSettings,
      permissions: {
        ...prev.permissions,
        ...(newSettings.permissions || {}),
      },
    }));
  };

  /**
   * Reset to default settings
   */
  const resetSettings = () => {
    setSettings(DEFAULT_SETTINGS);
  };

  /**
   * Check if budgeting is enabled at all
   * Returns false if budget type is 'none'
   * Always returns true since budgeting is always enabled
   */
  const isBudgetingEnabled = true; // Always enabled

  /**
   * Check if at least one budget category is enabled
   * Always returns true since all budgets are always enabled
   */
  const hasAnyBudgetEnabled = true; // Always enabled

  return {
    settings,
    updateBudgetType,
    updatePermissions,
    updateSettings,
    resetSettings,
    isBudgetingEnabled,
    hasAnyBudgetEnabled,
  };
}
